import ApiService, { ACCOUNTS_API_HOST } from './ApiService';
import { Capacitor } from '@capacitor/core';

/**
 * @override
 */
class AccountsService extends ApiService {
    /**
     * constructor
     */
    constructor() {
        super({ baseURL: ACCOUNTS_API_HOST });
    }
    /**
     * @param {Object} config Axios config
     * @param {String} uuid uuid of the account
     * @return {Object} Promise
     */
    getAccount(config, uuid) {
        return this.get(`/accounts/${uuid}`, config);
    }

    /**
     * @param {Object} config Axios config
     * @param {String} uuid uuid of the account
     * @return {Object} Promise
     */
    getAccountInterveners(config, uuid) {
        return this.get(`/accounts/${uuid}/interveners?$expand=account_intervener_role`, config);
    }

    /**
     * @param {Object} config Axios config
     * @param {String} uuid account uuid
     * @return {Object} Promise
     */
    getAccountBankAccounts(config, uuid) {
        return this.get(`/accounts/${uuid}/bank-accounts`, config);
    }

    /**
     * @param {Object} config Axios config
     * @param {String} uuid account uuid
     * @return {Object} Promise
     */
    getAccountProductsCategory(config, uuid) {
        return this.get(`/accounts/${uuid}/products`, config);
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid account uuid
     * @return {Object} Promise
     */
    updateAccount(config, body, uuid) {
        return this.patch(`/accounts/${uuid}`, body, config);
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid account uuid
     * @param {String} uuidIntervener Intervener uuid
     * @return {Object} Promise
     */
    updateAccountIntervener(config, body, uuid, uuidIntervener) {
        return this.patch(`/accounts/${uuid}/interveners/${uuidIntervener}`, body, config);
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {String} uuid account uuid
     * @param {String} uuidIntervener Intervener uuid
     * @param {Object} payload Data to patch
     * @return {Object} Promise
     */
    updateAccountIntervenerConfiguration(config, uuid, uuidIntervener, payload) {
        return this.patch(
            `/accounts/${uuid}/interveners/${uuidIntervener}/configuration`,
            payload,
            config
        );
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid account uuid
     * @param {String} uuidBankAccount bank account uuid
     * @return {Object} Promise
     */
    updateAccountBankAccounts(config, body, uuid, uuidBankAccount) {
        return this.patch(`/accounts/${uuid}/bank-accounts/${uuidBankAccount}`, body, config);
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {String} uuid account uuid
     * @param {String} uuidIntervener Intervener uuid
     * @return {Object} Promise
     */
    deleteAccountIntervener(config, uuid, uuidIntervener) {
        return this.delete(`/accounts/${uuid}/interveners/${uuidIntervener}`, config);
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {String} uuid account uuid
     * @param {String} uuidBankAccount bank account uuid
     * @return {Object} Promise
     */
    deleteAccountBankAccounts(config, uuid, uuidBankAccount) {
        return this.delete(`/accounts/${uuid}/bank-accounts/${uuidBankAccount}`, config);
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid account uuid
     * @return {Object} Promise
     */
    insertAccountIntervener(config, body, uuid) {
        if (Capacitor.isNativePlatform()) {
            body = { ...body, is_from_app: true };
        }

        return this.post(`/accounts/${uuid}/interveners/bulk`, body, config);
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid account uuid
     * @return {Object} Promise
     */
    insertAccountBankAccount(config, body, uuid) {
        return this.post(`/accounts/${uuid}/bank-accounts`, body, config);
    }

    /**
     * Update operation
     * @param {Object} config Axios config
     * @param {Object} operation operation data
     * @return {Object} Promise
     */
    updateOperation(config, operation) {
        return this.patch(`/operations/${operation.uuid}`, operation, config);
    }

    /**
     * Get accounts Documents Validations
     * @param {String} uuid Account UUID
     * @param {Object} config Axios config
     * @return {Object} Response with account documents validations data
     */
    getAccountDocumentsValidations(config, uuid) {
        return this.getList(`/accounts/${uuid}/document-validations`, config);
    }

    /**
     * @override
     */
    upload(payload) {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };

        const formData = new FormData();
        for (let i = 0; i < payload.files.length; i++) {
            formData.append('attachment', payload.files[i]);
            formData.append('filename', payload.filesName[i]);
            formData.append(
                'category',
                typeof payload.category === 'string' ? payload.category : payload.category[i]
            );
            formData.append(
                'subcategory',
                typeof payload.subcategory === 'string'
                    ? payload.subcategory
                    : payload.subcategory[i]
            );
            formData.append('validated', payload.validated);
            if (payload.uuid_bank_account) {
                formData.append('uuid_bank_account', payload.uuid_bank_account);
            }
            if (payload.associate_patron) {
                formData.append('associate_patron', payload.associate_patron);
            }
            if (payload.tmp_document) {
                formData.append('tmp_document', payload.tmp_document);
            }
        }
        return this.post(`/accounts/${payload.uuid}/documents`, formData, config);
    }

    /**
     * @override
     */
    getDocument(uuid, uuidDocument) {
        return this.get(`/accounts/${uuid}/documents/${uuidDocument}`, {
            responseType: 'blob'
        }).then(res => {
            return res;
        });
    }

    /**
     * Get account Documents
     * @param {String} uuid account UUID
     * @param {Object} config Axios config
     * @return {Object} Response with account documents data
     */
    getAccountDocuments(uuid, config) {
        return this.getList(`/accounts/${uuid}/documents?is_private_area=true`, config);
    }

    /**
     * Get document contributor
     * @param {String} uuid account UUID
     * @param {Object} config Axios config
     * @return {Object} Response with account documents data
     */
    getDocumentContributor(uuid, config) {
        return this.get(`/document/${uuid}/contributor`, config);
    }

    /**
     * Simulate operation
     * @param {Object} payload of the call
     * @return {Object} Response with intervener documents data
     */
    simulateOperation(payload) {
        return this.post('/operations/simulation', payload);
    }

    /**
     * Get account operations
     * @param {Object} config Axios config
     * @param {String} uuid account UUID
     * @return {Object} Response with account operations data
     */
    getAccountOperations(config, uuid) {
        return this.getList(`/accounts/${uuid}/operations`, config);
    }

    /**
     * Get account positions
     * @param {Object} config Axios config
     * @param {String} uuid account UUID
     * @return {Object} Response with account positions data
     */
    getAccountPositions(config, uuid) {
        return this.getList(`/accounts/${uuid}/positions`, config);
    }

    /**
     * Get account evolution
     * @param {Object} config Axios config
     * @param {String} uuid account UUID
     * @return {Object} Response with account evolution data
     */
    getAccountEvolution(config, uuid) {
        return this.getList(`/accounts/${uuid}/evolution?is_private_area=true`, config);
    }

    /**
     * Get account distribution
     * @param {Object} config Axios config
     * @param {String} uuid account UUID
     * @return {Object} Response with account distribution data
     */
    getAccountDistribution(config, uuid) {
        return this.getList(`/accounts/${uuid}/distribution`, config);
    }

    /**
     * @param {Object} config Axios config
     * @return {Object} Response with poll data
     */
    getPollsQuestions(config) {
        return this.getList('/polls', config);
    }

    /**
     * @param {String} uuid account UUID
     * @param {Object} payload payload
     * @return {Object} Response
     */
    createBeneficiaries(uuid, payload) {
        return this.post(`/accounts/${uuid}/beneficiaries`, payload);
    }

    /**
     * @param {String} uuid account UUID
     * @return {Object} Response
     */
    getBeneficiaries(uuid) {
        return this.getList(`/accounts/${uuid}/beneficiaries`);
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuid account uuid
     * @param {String} uuidBeneficiary beneficiary uuid
     * @return {Object} Promise
     */
    updateAccountBeneficiary(config, body, uuid, uuidBeneficiary) {
        return this.patch(`/accounts/${uuid}/beneficiaries/${uuidBeneficiary}`, body, config);
    }

    /**
     *
     * @param {Object} config Axios config
     * @param {String} uuid account uuid
     * @param {String} uuidBeneficiary beneficiary uuid
     * @return {Object} Promise
     */
    deleteAccountBeneficiary(config, uuid, uuidBeneficiary) {
        return this.delete(`/accounts/${uuid}/beneficiaries/${uuidBeneficiary}`, config);
    }

    /**
     *
     * @param {*} uuid
     * @param {*} uuidDocument
     * @returns {Object} Pomise
     */
    downloadAccountDocument(uuid, uuidDocument) {
        return this.get(`/accounts/${uuid}/documents/${uuidDocument}`, {
            responseType: 'blob'
        });
    }

    /**
     *
     * @param {Object} payload { uuid_account, uuid_intervener, answers: [{ id_poll, id_question, answer: [], answer_date }] }
     * @param {Object} config Axios config
     * @return {Object} Response with post result
     */
    insertPollAnswers(payload, config) {
        return this.post('/polls/answers', payload, config);
    }

    /**
     * Update account info
     * @param {String} uuid UUID of account
     * @param {Object} payload modified properties
     * @return {Object} Response data
     */
    updateAccountInfo(uuid, payload) {
        if (Capacitor.isNativePlatform()) {
            payload = { ...payload, is_from_app: true };
        }

        return this.patch(`/accounts/${uuid}`, payload);
    }

    /**
     * @param {String} uuid account UUID
     * @return {Object} Response
     */
    getAssociates(uuid) {
        return this.getList(`/accounts/${uuid}/associates`);
    }
    /**
     * @param {String} uuid account UUID
     * @return {Object} Response
     */
    createContributors(uuid, payload) {
        return this.post(`/accounts/${uuid}/contributors`, payload);
    }
    getAccountContriubutors(uuid) {
        return this.get(`/accounts/${uuid}/contributors`);
    }
    /**
     *
     * @param {*} payload {"uuid_account","date_from","date_to"}
     * @returns
     */
    positionStatutes(payload) {
        return this.post('/operations/position-statuses', payload);
    }

    createNotification(payload) {
        return this.post('/notifications', payload);
    }

    getAccountProductProfitabilityDetail(uuid, keyProduct) {
        return this.get(`/accounts/${uuid}/products/${keyProduct}/profitability-detail`);
    }

    getBankAccountOperations(uuid, uuidBankAccount) {
        return this.get(`/accounts/${uuid}/bank-accounts/${uuidBankAccount}/operations`);
    }
}

const accountsService = new AccountsService();

export default accountsService;
